import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    InsertableArchiveResult,
    EmailDocumentCategoryResult,
    CaseResult,
    RecordTypeResult,
    CarbonCopyContactResult,
    ValidateArchiveAndCaseResult,
    getInsertableArchives,
    getEmailDocumentCategories
} from '../../../services/api/dataServiceOutlookQuickSave';
import { IDocumentDetails } from '../../../@types/models';
import { QuickSearchItem } from '../components';
import { validateCaseWithArchive } from '../utils/caseValidationHelpers';
import { useQuickSaveSubmit } from './useQuickSaveSubmit';
import { navigateToImportArchive } from '../utils/importArchiveNavigation';
import { getPrioritizedCategory } from '../utils/categorySelectionHelpers';

const DEFAULT_CASE_CONNECTION = 1;

interface PreservedState {
    selectedArchive?: InsertableArchiveResult;
    selectedCategory?: EmailDocumentCategoryResult;
    selectedCase?: CaseResult;
    selectedRecordType?: RecordTypeResult;
    selectedCopyTo?: CarbonCopyContactResult[];
    title?: string;
    isRestricted?: boolean;
}

export interface QuickSaveState {
    // State values
    archives: InsertableArchiveResult[];
    selectedArchive: InsertableArchiveResult | null;
    isLoadingArchives: boolean;
    categories: EmailDocumentCategoryResult[];
    selectedCategory: EmailDocumentCategoryResult | null;
    isLoadingCategories: boolean;
    categoryError: string;
    selectedCases: QuickSearchItem[];
    selectedCase: CaseResult | null;
    selectedRecordType: RecordTypeResult | null;
    selectedCopyTo: CarbonCopyContactResult[];
    error: string;
    caseValidation: ValidateArchiveAndCaseResult | null;
    isValidatingCase: boolean;
    showRecordTypes: boolean;
    caseConnection: number;
    showCcContacts: boolean;
    title: string;
    isSubmitting: boolean;
    submitSuccess: boolean;
    submitError: string;
    // Action handlers
    handleArchiveChange: (archive: InsertableArchiveResult | null) => void;
    handleCategoryChange: (category: EmailDocumentCategoryResult | null) => void;
    handleCaseChange: (newCase: CaseResult | null) => void;
    handleRecordTypeChange: (recordType: RecordTypeResult | null) => void;
    handleCopyToChange: (copyTo: CarbonCopyContactResult[]) => void;
    handleSubmit: () => void;
    onChangeTitle: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

export const useQuickSaveState = (preservedState?: PreservedState): QuickSaveState => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Handle import archive navigation
    const handleImportArchive = useCallback((documentDetails: IDocumentDetails) => {
        navigateToImportArchive(navigate, documentDetails);
    }, [navigate]);
    const [archives, setArchives] = useState<InsertableArchiveResult[]>([]);
    const [selectedArchive, setSelectedArchive] = useState<InsertableArchiveResult | null>(preservedState?.selectedArchive || null);
    const [isLoadingArchives, setIsLoadingArchives] = useState(false);
    const [categories, setCategories] = useState<EmailDocumentCategoryResult[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<EmailDocumentCategoryResult | null>(preservedState?.selectedCategory || null);
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const [selectedCases] = useState<QuickSearchItem[]>([]);
    const [selectedCase, setSelectedCase] = useState<CaseResult | null>(preservedState?.selectedCase || null);
    const [selectedRecordType, setSelectedRecordType] = useState<RecordTypeResult | null>(preservedState?.selectedRecordType || null);
    const [selectedCopyTo, setSelectedCopyTo] = useState<CarbonCopyContactResult[]>(preservedState?.selectedCopyTo || []);
    const [error, setError] = useState<string>('');
    const [categoryError, setCategoryError] = useState<string>('');
    const [caseValidation, setCaseValidation] = useState<ValidateArchiveAndCaseResult | null>(null);
    const [isValidatingCase, setIsValidatingCase] = useState(false);
    const [showRecordTypes, setShowRecordTypes] = useState(false);
    const [caseConnection, setCaseConnection] = useState(DEFAULT_CASE_CONNECTION);
    const [showCcContacts, setShowCcContacts] = useState(false);
    const [title, setTitle] = useState(preservedState?.title || "");
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const loadCategories = useCallback(async (archiveRecno: number) => {
        setIsLoadingCategories(true);
        setCategoryError('');
        setSelectedCategory(null);
        setCategories([]);

        try {
            const result = await getEmailDocumentCategories(archiveRecno);
            setCategories(result);

            // Use prioritized category selection logic
            const prioritizedCategory = await getPrioritizedCategory(result);
            if (prioritizedCategory) {
                setSelectedCategory(prioritizedCategory);
            }
        } catch (err) {
            setCategoryError(err instanceof Error ? err.message : 'Failed to load categories');
        } finally {
            setIsLoadingCategories(false);
        }
    }, []);

    const setupArchiveState = useCallback((archive: InsertableArchiveResult) => {
        setSelectedArchive(archive);

        // Reset case selection when changing archives
        setSelectedCase(null);
        
        // Clear categories - they will be loaded only after case is selected
        setCategories([]);
        setSelectedCategory(null);

        const caseConnection = archive.CaseConnection ? archive.CaseConnection : DEFAULT_CASE_CONNECTION;
        setCaseConnection(Number(caseConnection));
        setShowCcContacts(archive.HasCcContacts);
    }, []);

    const loadArchives = useCallback(async () => {
        setIsLoadingArchives(true);
        setError('');

        try {
            const result = await getInsertableArchives();
            setArchives(result);

            // If we have preserved state with an archive, reload its categories
            if (preservedState?.selectedArchive) {
                await loadCategories(preservedState.selectedArchive.Recno);

                // Restore the preserved category after categories are loaded
                if (preservedState.selectedCategory) {
                    setSelectedCategory(preservedState.selectedCategory);
                }

                // Restore the preserved case
                if (preservedState.selectedCase) {
                    setSelectedCase(preservedState.selectedCase);
                }

                // Restore the preserved record type
                if (preservedState.selectedRecordType) {
                    setSelectedRecordType(preservedState.selectedRecordType);
                }

                const caseConnection = preservedState.selectedArchive.CaseConnection
                    ? preservedState.selectedArchive.CaseConnection
                    : DEFAULT_CASE_CONNECTION;
                setCaseConnection(Number(caseConnection));
                setShowCcContacts(preservedState.selectedArchive.HasCcContacts);
            }
            // If only one archive and no preserved state, select it as default
            else if (result.length === 1) {
                setupArchiveState(result[0]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load archives');
        } finally {
            setIsLoadingArchives(false);
        }
    }, [setupArchiveState, loadCategories, preservedState]);

    const setTitleFromOfficeToState = useCallback(() => {
        // Only set title from Office if we don't have preserved title
        if (!preservedState?.title && Office.context.mailbox?.item?.normalizedSubject) {
            setTitle(Office.context.mailbox.item.normalizedSubject);
        }
    }, [preservedState]);

    useEffect(() => {
        void loadArchives();
        setTitleFromOfficeToState();
    }, [loadArchives, setTitleFromOfficeToState]);

    const handleArchiveChange = useCallback((currentArchive: InsertableArchiveResult | null) => {
        if (currentArchive) {
            setupArchiveState(currentArchive);
        } else {
            setCategories([]);
            setSelectedCategory(null);
        }
    }, [setupArchiveState]);

    const handleCategoryChange = useCallback((newCategory: EmailDocumentCategoryResult | null) => {
        setSelectedCategory(newCategory);
    }, []);

    // Helper function to reset case-related state
    const resetCaseRelatedState = useCallback(() => {
        setSelectedCategory(null);
        setSelectedRecordType(null);
        setCaseValidation(null);
        setShowRecordTypes(false);
    }, []);

    const handleCaseChange = useCallback((newCase: CaseResult | null) => {
        void (async () => {
            setSelectedCase(newCase);
            resetCaseRelatedState();

            if (newCase && selectedArchive) {
                await validateCaseWithArchive(selectedArchive.Recno, newCase.Recno, {
                    onValidationStart: () => setIsValidatingCase(true),
                    onValidationEnd: () => setIsValidatingCase(false),
                    onValidationSuccess: (validation) => setCaseValidation(validation),
                    onValidationFailed: (validation, errorMessage) => {
                        setCaseValidation(validation);
                        setCategoryError(errorMessage);
                    },
                    onShowRecordTypes: setShowRecordTypes,
                    onLoadCategories: loadCategories,
                    onError: (errorMessage) => setCategoryError(errorMessage)
                });
            }
        })();
    }, [selectedArchive, resetCaseRelatedState, loadCategories]);

    const handleRecordTypeChange = useCallback((newRecordType: RecordTypeResult | null) => {
        setSelectedRecordType(newRecordType);
    }, []);

    const handleCopyToChange = useCallback((newCopyTo: CarbonCopyContactResult[]) => {
        setSelectedCopyTo(newCopyTo);
    }, []);

    // Submit hook
    const submitHook = useQuickSaveSubmit({
        title,
        selectedArchive: selectedArchive!,
        selectedCategory: selectedCategory!,
        selectedCase,
        selectedRecordType,
        selectedCopyTo,
        caseValidation,
        onSaveSuccess: () => {
            setSubmitSuccess(true);
            setSubmitError('');
        },
        onSaveError: (error: string) => {
            setSubmitError(error);
            setSubmitSuccess(false);
        },
        onImportArchive: handleImportArchive
    });

    const handleSubmit = useCallback(() => {
        if (!selectedArchive || !selectedCategory || !selectedCase) {
            setSubmitError(t('Missing required fields for submission'));
            return;
        }

        setSubmitError('');
        setSubmitSuccess(false);
        submitHook.handleSubmit();
    }, [selectedArchive, selectedCategory, selectedCase, submitHook, t]);

    const onChangeTitle = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setTitle(event.target.value);
    }, []);

    return {
        // State values
        archives, selectedArchive, isLoadingArchives, categories, selectedCategory, isLoadingCategories, selectedCases, selectedCase, selectedRecordType,
        selectedCopyTo, error, categoryError, caseValidation, isValidatingCase, showRecordTypes, caseConnection, showCcContacts, title,
        isSubmitting: submitHook.isSubmitting, submitSuccess, submitError,
        // Action handlers
        handleArchiveChange, handleCategoryChange, handleCaseChange, handleRecordTypeChange, handleCopyToChange, handleSubmit, onChangeTitle
    };
};
